import { el, mount, unmount } from 'redom';

import application from 'application';

import serverInterface from 'server-interface';

import externalEventCodes from 'event-system/codes/external';

/**
 * Every render method shall have one unmount method and update method
 * 
 * 
 * 
 * */

import * as meetingStatuses from 'constants/meeting-statuses';
import * as meetingLiveStatuses from 'constants/meeting-live-statuses';

import styleAsString from './styles/index.scss?inline';

class SingleProducerMeeting extends HTMLElement {

  constructor() {

    super();

    this.attachShadow({ mode: 'open' });

    this.currentUserId = null;
    this.postId = null;
    this.post = null;
    this.meeting = null;
    this.loaded = false;
    this.inEditor = false;

    // elements
    this.spinner = null;
    this.readOnlyView = null;
    this.createdView = null;
    this.liveView = null;
    this.endedView = null;

    if (application.isAuthenticated() === true) {
      this.currentUserId = application.getUser()._id;
    }

    mount(this.shadowRoot, this.getStyleEl());

  }

  connectedCallback() {

    this.postId = this.getAttribute('postid');
    this.inEditor = this.getAttribute('editor') === 'true';
    this.viewerMode = isDisplayedInPostBrowser(this);

    this.initialize();

  }

  disconnectedCallback() {

    this.unregisterEventListeners();

    if (this.meeting !== null) {
      this.unmountCurrentMount();
    }

  }

  registerEventListeners(postId) {

    this.registerForMeetingEndedEvent(postId);
    this.registerForMeetingMutedEvent(postId);
    this.registerForMeetingUnmutedEvent(postId);

  }

  registerForMeetingEndedEvent(postId) {

    async function handleMeetingEndedEvent(data) {

      this.meeting = data.meeting;

      this.unmountPreviousMount();
      await this.mountRoot();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.SINGLE_PRODUCER_MEETING_ENDED}:${postId}`, handleMeetingEndedEvent.bind(this));

  }

  registerForMeetingMutedEvent(postId) {

    function handleMeetingMutedEvent(data) {

      this.meeting = data.meeting;

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.SINGLE_PRODUCER_MEETING_MUTED}:${postId}`, handleMeetingMutedEvent.bind(this));

  }

  registerForMeetingUnmutedEvent(postId) {

    function handleMeetingUnmutedEvent(data) {

      this.meeting = data.meeting;

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.SINGLE_PRODUCER_MEETING_UNMUTED}:${postId}`, handleMeetingUnmutedEvent.bind(this));

  }

  unregisterEventListeners() {

    application.eventSystem.internal.offAllOn(this);

  }

  getStyleEl() {

    const styleCode = `

      ${styleAsString}

    `;

    const style = el('style', styleCode);

    return style;

  }

  async handleMeetingEndedEvent() {

    this.unmountPreviousMount();
    await this.mountRoot();

  }

  async loadMeeting() {

    this.meeting = await application.stores.singleProducerMeetings.getSingleProducerMeetingByPostId(this.postId);

    this.loaded = true;

  }

  async loadPost() {

    this.post = await serverInterface.startup.community.getFullPostById(this.postId);

  }

  async initialize() {

    this.mountSpinner();

    await Promise.all([this.loadMeeting(), this.loadPost()]);

    this.unmountSpinner();

    await this.mountRoot();

  }

  mountSpinner() {

    this.spinner = el('ht-spinner');

    mount(this.shadowRoot, this.spinner);

  }

  unmountSpinner() {

    if (this.spinner === null) {
      return;
    }

    unmount(this.shadowRoot, this.spinner);

  }

  async mountLiveView() {

    const { default: LiveView } = await import('./live-view');

    this.liveView = new LiveView(this.currentUserId, this.meeting, this.post, this.viewerMode);

    this.liveView.el.addEventListener('meeting-ended', this.handleMeetingEndedEvent.bind(this));

    mount(this.shadowRoot, this.liveView);

  }

  unmountLiveView() {

    unmount(this.shadowRoot, this.liveView);

    this.liveView = null;

  }

  async mountMeetingNotStartedView() {

    const { default: MeetingNotStartedView } = await import('./meeting-not-started-view');

    this.meetingStartedView = new MeetingNotStartedView(this.meeting);

    mount(this.shadowRoot, this.meetingStartedView);

  }

  unmountMeetingNotStartedView() {

    unmount(this.shadowRoot, this.meetingStartedView);

    this.meetingStartedView = null;

  }

  async mountEndedView() {

    const { default: EndedView } = await import('./ended-view');

    this.endedView = new EndedView(this.meeting);

    mount(this.shadowRoot, this.endedView);

    if (this.autoPlay === true) {
      this.endedView.playVideo();
    }

  }

  unmountEndedView() {

    unmount(this.shadowRoot, this.endedView);

    this.endedView = null;

  }

  async mountCreatedView() {

    const { default: CreatedView } = await import('./created-view');

    this.createdView = new CreatedView(this.currentUserId, this.meeting);

    this.createdView.el.addEventListener('meeting-went-live', this.handleMeetingWentLive.bind(this));

    mount(this.shadowRoot, this.createdView);

  }

  unmountCreatedView() {

    unmount(this.shadowRoot, this.createdView);

    this.createdView = null;

  }

  async mountRoot() {

    if (this.meeting.status === meetingStatuses.CREATED) {
      await this.mountCreatedView();
    } else if (this.meeting.status === meetingStatuses.LIVE) {
      await this.mountLiveView();
    } else {
      await this.mountEndedView();
    }

  }

  unmountPreviousMount() {

    if (this.meeting.status === meetingStatuses.LIVE) {
      this.unmountCreatedView();
    } else if (this.meeting.status === meetingStatuses.ENDED) {
      this.unmountLiveView();
    }

  }

  unmountCurrentMount() {

    if (this.meeting.status === meetingStatuses.CREATED) {
      this.unmountCreatedView();
    } else if (this.meeting.status === meetingStatuses.LIVE) {
      this.unmountLiveView();
    } else if (this.meeting.status === meetingStatuses.ENDED) {
      this.unmountEndedView();
    }

  }

  async handleMeetingWentLive() {

    this.meeting.status = meetingStatuses.LIVE;
    this.meeting.liveStatus = meetingLiveStatuses.CONNECTED;

    this.unmountCreatedView();
    await this.mountLiveView();

  }

  visible() {

  }

  invisible() {

  }

}

function isDisplayedInPostBrowser(element) {

  const postBrowserMain = document.getElementById('posts-browser-main');

  let _element = element;

  while (_element.parentNode) {
    if (_element.parentNode === postBrowserMain) {
      return true;
    }
    _element = _element.parentNode;
  }

  return false;

}

export default SingleProducerMeeting;